import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MailCheck, Loader2, RefreshCw } from "lucide-react";

/** "Check your inbox" state shown after registering, or when an unverified user tries to log in. */
export default function EmailVerificationNotice({ email, message, initialCooldown = 60 }) {
  const [cooldown, setCooldown] = useState(initialCooldown);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      setCooldown(data.cooldown_remaining || 60);
      toast.success("Confirmation link sent — check your inbox and spam folder");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 rounded-2xl border-2 border-accent/30 bg-accent/5" data-testid="verify-notice">
      <p className="font-display text-lg font-semibold inline-flex items-center gap-2">
        <MailCheck className="h-5 w-5 text-accent" /> Check your email
      </p>
      <p className="text-sm mt-2" data-testid="verify-notice-message">
        {message || `We've sent a confirmation link to ${email}. Click it to finish setting up your account.`}
      </p>
      <p className="text-xs text-muted-foreground mt-2">
        The link works once and expires in 24 hours. Can't see it? Check your spam folder.
      </p>
      <button onClick={resend} disabled={busy || cooldown > 0}
        className="mt-4 px-5 py-2.5 rounded-full bg-accent text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="verify-resend-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend the link"}
      </button>
    </div>
  );
}
